import { useLanguage } from '@/contexts/LanguageContext';
import { ImageWithFallback } from '@/app/components/figma/ImageWithFallback';
import { useState, useEffect } from 'react';
import { getContactInfo, type ContactInfo } from '@/services/api';
import { assetApi, processAssetMarkers, type Asset } from '@/services/assetApi';

export function Contact() {
  const { t, language } = useLanguage();
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        setLoading(true);
        const [info, assetsList] = await Promise.all([
          getContactInfo(),
          assetApi.getAssets().catch(err => {
            console.error('Failed to load assets:', err);
            return []; // Return empty array if assets fail to load
          })
        ]);
        if (info) {
          setContactInfo(info);
        }
        setAssets(assetsList);
      } catch (error) {
        console.error('Failed to load contact info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContactInfo();
  }, []);

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-gray-400">Loading...</div>
          </div>
        ) : (
          <>
        {/* Two Column Layout with Golden Ratio - Single column on mobile */}
        <div className="flex flex-col md:flex-row gap-[30px]">
          {/* Desktop: Left Column - 38.2% - Right Aligned - Labels Only */}
          <div className="hidden md:block text-right space-y-6 md:w-[38.2%]">
            <p className="text-gray-900">{t('contact.office')}</p>
            <p className="text-gray-900">{t('contact.phone')}</p>
            <p className="text-gray-900">{t('contact.email')}</p>
            <p className="text-gray-900">{t('contact.online')}</p>
          </div>

          {/* Desktop: Right Column - 61.8% - Left Aligned - Values + Image + Company Info */}
          <div className="md:w-[61.8%]">
            {/* Contact Values - Desktop */}
            <div className="hidden md:block space-y-6 mb-12">
              {contactInfo?.officeLink ? (
                <a 
                  href={contactInfo.officeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-gray-400 hover:text-black transition-colors"
                >
                  {contactInfo.address}
                </a>
              ) : (
                <p className="text-gray-600">{contactInfo?.address || '4025 Debrecen, Hatvan u. 6. III./11'}</p>
              )}
              <p className="text-gray-600">{contactInfo?.phone || '+36 30 352 99 59'}</p>
              <p className="text-gray-600">{contactInfo?.email || 'info@zsalako.com'}</p>
              {contactInfo?.onlineText && (
                <div 
                  className="text-gray-600 [&_a]:text-gray-400 [&_a]:hover:text-black [&_a]:transition-colors [&_a]:no-underline [&_img]:inline-block [&_img]:align-middle"
                  dangerouslySetInnerHTML={{ __html: processAssetMarkers(contactInfo.onlineText, assets, language) }}
                />
              )}
            </div>

            {/* Contact Values - Mobile: Label + Value pairs */}
            <div className="md:hidden space-y-6 mb-12">
              <div>
                <p className="text-gray-900 mb-1">{t('contact.office')}</p>
                {contactInfo?.officeLink ? (
                  <a 
                    href={contactInfo.officeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-gray-400 hover:text-black transition-colors"
                  >
                    {contactInfo.address}
                  </a>
                ) : (
                  <p className="text-gray-600">{contactInfo?.address || '4025 Debrecen, Hatvan u. 6. III./11'}</p>
                )}
              </div>
              <div>
                <p className="text-gray-900 mb-1">{t('contact.phone')}</p>
                <p className="text-gray-600">{contactInfo?.phone || '+36 30 352 99 59'}</p>
              </div>
              <div>
                <p className="text-gray-900 mb-1">{t('contact.email')}</p>
                <p className="text-gray-600">{contactInfo?.email || 'info@zsalako.com'}</p>
              </div>
              {contactInfo?.onlineText && (
                <div>
                  <p className="text-gray-900 mb-1">{t('contact.online')}</p>
                  <div 
                    className="text-gray-600 [&_a]:text-gray-400 [&_a]:hover:text-black [&_a]:transition-colors [&_a]:no-underline [&_img]:inline-block [&_img]:align-middle"
                    dangerouslySetInnerHTML={{ __html: processAssetMarkers(contactInfo.onlineText, assets, language) }}
                  />
                </div>
              )}
            </div>

            {/* Company Details */}
            <div className="mb-12">
              <p className="text-xl text-gray-900 mb-3 font-semibold">
                {contactInfo?.companyName || (
                  <><span className="font-semibold">ZSALAKÓ</span> ÉPÍTŐMŰHELY KFT.</>
                )}
              </p>
              <div className="text-gray-600 space-y-1">
                {contactInfo?.companyLocation && (
                  <p>{t('contact.companyLocation')}: {contactInfo.companyLocation}</p>
                )}
                {contactInfo?.taxNumber && (
                  <p>{t('contact.taxNumber')}: {contactInfo.taxNumber}</p>
                )}
                {contactInfo?.registrationNumber && (
                  <p>{t('contact.registrationNumber')}: {contactInfo.registrationNumber}</p>
                )}
                {/* Fallback ha nincsenek megadva az adatbázisban */}
                {!contactInfo?.companyLocation && !contactInfo?.taxNumber && !contactInfo?.registrationNumber && (
                  <>
                    <p>Telephely: 4033 Debrecen, Herpay Gábor u. 18.</p>
                    <p>Adószám: 20388324-2-09</p>
                    <p>Cj.: 4545456645</p>
                  </>
                )}
              </div>
            </div>

            {/* Removed Stability Badge - moved to About page */}
          </div>
        </div>
          </>
        )}
      </div>
    </section>
  );
}